
import React from "react";
import { OptimizedLogo } from "./performance/OptimizedLogo";
import { cn } from "@/lib/utils";

type LogoVariant = "horizontal" | "icon" | "compact";

interface BrandLogoProps {
  variant?: LogoVariant;
  className?: string;
  size?: number; // 'size' will map to HEIGHT only for landscape logo
  priority?: boolean; // For critical images that should load immediately
}

// Map logo variants to uploaded URLs
const logoSources: Record<LogoVariant, string> = {
  horizontal: "/lovable-uploads/85d569bd-2e7a-45ef-a959-46b09ce2c956.png",
  compact: "/lovable-uploads/e9aed06c-410b-4cdc-bd18-6cae7e9664c6.png",
  icon: "/lovable-uploads/359c980f-f29c-4c3e-ab30-3d2034f64fb1.png",
};

export const BrandLogo: React.FC<BrandLogoProps> = ({
  variant = "horizontal",
  className = "",
  size = 40,
  priority = false,
}) => {
  const src = logoSources[variant];

  const alt =
    variant === "icon"
      ? "Dialgia icon logo"
      : variant === "compact"
      ? "Dialgia compact logo"
      : "Dialgia logo";

  const [imgError, setImgError] = React.useState(false);

  if (!src || imgError) {
    return (
      <div
        className={
          `bg-gradient-to-br from-blue-400 to-green-300 rounded-lg flex items-center justify-center ` + className
        }
        style={
          variant === "icon"
            ? { width: size, height: size }
            : { height: size, width: "auto" }
        }
        aria-label="Dialgia"
      />
    );
  }

  // Calculate explicit dimensions for proper aspect ratio
  const getLogoDimensions = () => {
    if (variant === "icon") {
      return { width: size, height: size };
    }
    
    // For horizontal logos, calculate width based on height to maintain aspect ratio
    // Assuming a 3:1 aspect ratio for horizontal logos
    const aspectRatio = 3;
    return { width: size * aspectRatio, height: size };
  };

  const { width: logoWidth, height: logoHeight } = getLogoDimensions();

  return (
    <OptimizedLogo
      src={src}
      alt={alt}
      className={cn("block object-contain", className)}
      width={logoWidth}
      height={logoHeight}
      priority={priority}
      onError={() => setImgError(true)}
      isLogo={true}
    />
  );
};

export default BrandLogo;
